/**
 * Shared category styling for workflow step nodes and the node library.
 *
 * Each category defines:
 *  - icon: Phosphor icon component name (rendered by consumers)
 *  - badge: Tailwind classes for small icon badges (library list, node corner badge)
 *  - header: Tailwind classes for the node header bar (bg + text)
 *  - border: Tailwind classes for the node card border when selected
 *  - iconColor: Tailwind text color class for the corner icon
 */

export interface CategoryStyle {
  /** Tailwind classes for badge/chip appearance (bg, text, border) */
  badge: string;
  /** Tailwind classes for the node header background + text */
  header: string;
  /** Tailwind classes for the selected node border + ring */
  selectedBorder: string;
  /** Tailwind text color for the corner icon */
  iconColor: string;
}

export const categoryStyles: Record<string, CategoryStyle> = {
  'Data Transform': {
    badge:
      'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800',
    header:
      'bg-violet-100 dark:bg-violet-900 text-violet-900 dark:text-violet-100',
    selectedBorder:
      'border-violet-500 dark:border-violet-400 ring-2 ring-violet-500/20',
    iconColor: 'text-violet-600 dark:text-violet-400',
  },
  'DataSource Adapter': {
    badge:
      'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800',
    header:
      'bg-violet-100 dark:bg-violet-900 text-violet-900 dark:text-violet-100',
    selectedBorder:
      'border-violet-500 dark:border-violet-400 ring-2 ring-violet-500/20',
    iconColor: 'text-violet-600 dark:text-violet-400',
  },
  'Data Access': {
    badge:
      'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    header: 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100',
    selectedBorder:
      'border-blue-500 dark:border-blue-400 ring-2 ring-blue-500/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  'DataSource Writer': {
    badge:
      'bg-[#e8d5b0] dark:bg-[#3d2a10]/30 text-[#5c3d1e] dark:text-[#e8d5b0] border-[#c4a882] dark:border-[#4d3518]',
    header: 'bg-[#e8d5b0] dark:bg-[#3d2a10] text-[#3d2a10] dark:text-[#e8d5b0]',
    selectedBorder:
      'border-[#8b6040] dark:border-[#c4a882] ring-2 ring-[#8b6040]/20',
    iconColor: 'text-[#7a5030] dark:text-[#c4a882]',
  },
  Transformation: {
    badge:
      'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    header:
      'bg-purple-100 dark:bg-purple-900 text-purple-900 dark:text-purple-100',
    selectedBorder:
      'border-purple-500 dark:border-purple-400 ring-2 ring-purple-500/20',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  Integration: {
    badge:
      'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    header: 'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100',
    selectedBorder:
      'border-green-500 dark:border-green-400 ring-2 ring-green-500/20',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  'Control Flow': {
    badge:
      'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
    header:
      'bg-orange-100 dark:bg-orange-900 text-orange-900 dark:text-orange-100',
    selectedBorder:
      'border-orange-500 dark:border-orange-400 ring-2 ring-orange-500/20',
    iconColor: 'text-orange-600 dark:text-orange-400',
  },
  Actions: {
    badge:
      'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
    header: 'bg-rose-100 dark:bg-rose-900 text-rose-900 dark:text-rose-100',
    selectedBorder:
      'border-rose-500 dark:border-rose-400 ring-2 ring-rose-500/20',
    iconColor: 'text-rose-600 dark:text-rose-400',
  },
  Utility: {
    badge:
      'bg-slate-100 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800',
    header: 'bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-slate-100',
    selectedBorder:
      'border-slate-500 dark:border-slate-400 ring-2 ring-slate-500/20',
    iconColor: 'text-slate-600 dark:text-slate-400',
  },
  Trigger: {
    badge:
      'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    header: 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100',
    selectedBorder:
      'border-amber-500 dark:border-amber-400 ring-2 ring-amber-500/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  'AI & Agents': {
    badge:
      'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800',
    header: 'bg-cyan-100 dark:bg-cyan-900 text-cyan-900 dark:text-cyan-100',
    selectedBorder:
      'border-cyan-500 dark:border-cyan-400 ring-2 ring-cyan-500/20',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
  },
  'Computer Use': {
    badge:
      'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
    header:
      'bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100',
    selectedBorder:
      'border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-500/20',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
  },
  Debug: {
    badge:
      'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
    header: 'bg-red-100 dark:bg-red-900 text-red-900 dark:text-red-100',
    selectedBorder: 'border-red-500 dark:border-red-400 ring-2 ring-red-500/20',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  Communications: {
    badge:
      'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800',
    header: 'bg-teal-100 dark:bg-teal-900 text-teal-900 dark:text-teal-100',
    selectedBorder:
      'border-teal-500 dark:border-teal-400 ring-2 ring-teal-500/20',
    iconColor: 'text-teal-600 dark:text-teal-400',
  },
};

const defaultStyle = categoryStyles['Utility'];

export function getCategoryStyle(category: string | undefined): CategoryStyle {
  return categoryStyles[category ?? ''] ?? defaultStyle;
}
